'use client';

import { useEffect, useRef } from 'react';

import { useNativeDataContext } from '@onyx/help-hub/hooks';
import {
  sendEvent,
  setSessionProperty,
  setUserId,
  useInitObservability,
} from '@onyx/observability-v2/client';
import { usePathname } from 'next/navigation';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { useSession } from 'next-auth/react';

import type { Category } from '../models/issues/issue';
import { selectStore } from '../state/location-slice';
import { useStore } from '../state/store-provider';
import { trackBrowserBack } from './help-hub-error-events';
import { withSupportChannel } from './help-hub-event-payload';
import { useTeamsContext } from './teams-context';

type SupportChannel = NonNullable<Category['supportChannel']>;
export type HelpHubPlatform = 'Browser' | 'Honeywell' | 'Teams';
type IssueOption = {
  id?: string;
  key?: string;
  name: string;
  supportChannel?: SupportChannel;
};
type IssueSubmission = {
  issueId: string;
  submissionId?: string;
  area?: IssueOption;
  department?: IssueOption;
  category?: IssueOption;
  hasWarning: boolean;
  isEscalation?: boolean;
  isFeedback?: boolean;
};

export const getHelpHubUserId = (userIdentifier?: string | null) =>
  userIdentifier?.split('@')[0] || undefined;

export const getHelpHubPlatform = ({
  isInTeams,
  isNativeDataEnabled,
}: {
  isInTeams: boolean;
  isNativeDataEnabled: boolean;
}): HelpHubPlatform => {
  if (isNativeDataEnabled) {
    return 'Honeywell';
  }

  if (isInTeams) {
    return 'Teams';
  }

  return 'Browser';
};

export const trackIssueSubmissionSuccess = ({
  submissionId,
  area,
  category,
  department,
  hasWarning,
  isEscalation = false,
  isFeedback = false,
  issueId,
}: IssueSubmission) =>
  sendEvent('help-hub.issue.create.submitted', {
    'issue.id': issueId,
    'issue.area': area?.name,
    'issue.department': department?.name,
    'issue.category': category?.name,
    // eslint-disable-next-line camelcase
    ...(submissionId ? { submission_id: submissionId } : {}),
    ...withSupportChannel(category?.supportChannel),
    'issue.is_escalation': isEscalation,
    'issue.has_warning': hasWarning,
    'issue.is_feedback': isFeedback,
  });

export const HelpHubObservability = () => {
  const { data: session } = useSession();
  const pathname = usePathname();
  const { store } = useStore(selectStore);
  const { isInTeams } = useTeamsContext();
  const { enabled: isNativeDataEnabled } = useNativeDataContext();
  const previousPath = useRef<string | null>(null);
  const isPopStateNavigation = useRef(false);
  const userId = getHelpHubUserId(session?.user?.email ?? session?.user?.id);
  const storeId = store?.id;
  const platform = getHelpHubPlatform({ isInTeams, isNativeDataEnabled });

  useInitObservability({ userId });

  useEffect(() => {
    if (userId) {
      setUserId(userId);
    }
    setSessionProperty('userId', userId);
  }, [userId]);

  useEffect(() => {
    setSessionProperty('storeId', storeId);
  }, [storeId]);

  useEffect(() => {
    setSessionProperty('platform', platform);
  }, [platform]);

  useEffect(() => {
    const onPopState = () => {
      isPopStateNavigation.current = true;
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(() => {
    /* istanbul ignore next */
    if (!pathname) {
      /* istanbul ignore next */
      return;
    }

    if (previousPath.current && isPopStateNavigation.current) {
      trackBrowserBack({
        fromPath: previousPath.current,
        toPath: pathname,
      });
      isPopStateNavigation.current = false;
    }

    previousPath.current = pathname;
  }, [pathname]);

  return null;
};
